import React from 'react';
import * as XLSX from 'xlsx';
import Loader from '../../component/common/Loader';
import CONSTANT from '../../constant';
import {
  bulkUploadEmployeeAuthority,
  bulkUploadEmployeeAbsence,
  bulkUploadEmployeeQualificationTraining,
  bulkUploadEmployeeOtherDetails,
  bulkUploadAcademicServiceDetails,
  getAppraisalCycleList,
} from '../../actions/AppraisalCycleAction';
import { formatDateDDMMYY } from '../../utils';

const TABS = [
  { key: 'authority', label: 'Authority Mapping', icon: 'fa-users', cycleBased: true, color: '#3b82f6' },
  { key: 'absence', label: 'Period of Absence', icon: 'fa-calendar-minus-o', cycleBased: true, color: '#3b82f6' },
  { key: 'qualification', label: 'Qualifications & Training', icon: 'fa-graduation-cap', cycleBased: true, color: '#3b82f6' },
  { key: 'otherDetails', label: 'Other Details', icon: 'fa-file-text-o', cycleBased: true, color: '#3b82f6' },
  { key: 'academic', label: 'Academic & Service', icon: 'fa-university', cycleBased: false, color: '#3b82f6' },
];

const SECTION_CONFIG: any = {
  authority: {
    title: 'Employee Authority Mapping',
    apiAction: bulkUploadEmployeeAuthority,
    columns: [
      { key: 'employeeNumber', label: 'Employee Number', required: true },
      { key: 'reportingManagerEmployeeNumber', label: 'Reporting Manager Emp No' },
      { key: 'reviewingManagerEmployeeNumber', label: 'Reviewing Manager Emp No' },
      { key: 'acceptingManagerEmployeeNumber', label: 'Accepting Manager Emp No' },
    ],
    sampleData: [
      { employeeNumber: 'EMP001', reportingManagerEmployeeNumber: 'EMP010', reviewingManagerEmployeeNumber: 'EMP020', acceptingManagerEmployeeNumber: 'EMP030' },
      { employeeNumber: 'EMP002', reportingManagerEmployeeNumber: 'EMP010', reviewingManagerEmployeeNumber: 'EMP020', acceptingManagerEmployeeNumber: 'EMP030' },
    ],
    validate: (item: any) => !!item.employeeNumber && (!!item.reportingManagerEmployeeNumber || !!item.reviewingManagerEmployeeNumber || !!item.acceptingManagerEmployeeNumber),
  },
  absence: {
    title: 'Period of Absence on Leave',
    apiAction: bulkUploadEmployeeAbsence,
    columns: [
      { key: 'employeeNumber', label: 'Employee Number', required: true },
      { key: 'absenceType', label: 'Type (leave/others)', required: true },
      { key: 'periodFrom', label: 'Period From' },
      { key: 'periodTo', label: 'Period To' },
      { key: 'leaveType', label: 'Leave Type' },
      { key: 'remarks', label: 'Remarks' },
    ],
    sampleData: [
      { employeeNumber: 'EMP001', absenceType: 'leave', periodFrom: '2025-06-01', periodTo: '2025-06-15', leaveType: 'Earned Leave', remarks: 'Annual leave' },
      { employeeNumber: 'EMP001', absenceType: 'others', periodFrom: '2025-09-01', periodTo: '2025-09-10', leaveType: 'Deputation', remarks: 'Training deputation' },
    ],
    validate: (item: any) => !!item.employeeNumber && !!item.absenceType,
  },
  qualification: {
    title: 'Qualifications & Training',
    apiAction: bulkUploadEmployeeQualificationTraining,
    columns: [
      { key: 'employeeNumber', label: 'Employee Number', required: true },
      { key: 'recordType', label: 'Type (qualification/training)', required: true },
      { key: 'details', label: 'Details / Subject' },
      { key: 'institution', label: 'Institution / Institute' },
      { key: 'marks', label: 'Marks' },
      { key: 'dateFrom', label: 'Date From' },
      { key: 'dateTo', label: 'Date To' },
    ],
    sampleData: [
      { employeeNumber: 'EMP001', recordType: 'qualification', details: 'MBA Finance', institution: 'IIM Ahmedabad', marks: '85', dateFrom: '', dateTo: '' },
      { employeeNumber: 'EMP001', recordType: 'training', details: 'Project Management', institution: 'NIFM', marks: '', dateFrom: '2025-07-01', dateTo: '2025-07-15' },
    ],
    validate: (item: any) => !!item.employeeNumber && !!item.recordType,
  },
  otherDetails: {
    title: 'Other Details',
    apiAction: bulkUploadEmployeeOtherDetails,
    columns: [
      { key: 'employeeNumber', label: 'Employee Number', required: true },
      { key: 'awardsHonours', label: 'Awards / Honours' },
      { key: 'parNotWrittenCount', label: 'PAR Not Written Count' },
      { key: 'propertyReturnDate', label: 'Property Return Date' },
      { key: 'lastMedicalExamDate', label: 'Last Medical Exam Date' },
    ],
    sampleData: [
      { employeeNumber: 'EMP001', awardsHonours: 'Best Employee 2025', parNotWrittenCount: '0', propertyReturnDate: '2025-12-31', lastMedicalExamDate: '2025-06-15' },
      { employeeNumber: 'EMP002', awardsHonours: '', parNotWrittenCount: '2', propertyReturnDate: '2025-12-31', lastMedicalExamDate: '' },
    ],
    validate: (item: any) => !!item.employeeNumber,
  },
  academic: {
    title: 'Academic & Service Details',
    apiAction: bulkUploadAcademicServiceDetails,
    columns: [
      { key: 'employeeNumber', label: 'Employee Number', required: true },
      { key: 'academicQualifications', label: 'Academic Qualifications' },
      { key: 'postHeld', label: 'Name of Post Held' },
      { key: 'gradeOfPost', label: 'Grade of Post' },
      { key: 'dateOfAppointment', label: 'Date of Appointment in Post' },
      { key: 'presentPayScale', label: 'Present Pay & Pay Scale' },
      { key: 'dateOfContinuousAppointment', label: 'Date Continuous Appt. in Enterprise' },
      { key: 'dateOfFirstPublicAppointment', label: 'Date of First Public Enterprise Appt.' },
      { key: 'scaleOfPayOnFirstAppointment', label: 'Scale of Pay on First Appt.' },
    ],
    sampleData: [
      { employeeNumber: 'EMP001', academicQualifications: 'B.Tech Civil', postHeld: 'Senior Engineer', gradeOfPost: 'E4', dateOfAppointment: '2018-04-01', presentPayScale: '56100-177500', dateOfContinuousAppointment: '2015-06-15', dateOfFirstPublicAppointment: '2015-06-15', scaleOfPayOnFirstAppointment: '40000-140000' },
    ],
    validate: (item: any) => !!item.employeeNumber,
  },
};

class BulkUploadAuthority extends React.Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = {
      activeTab: 'authority',
      rawExcel: [],
      showLoader: false,
      uploadComplete: false,
      cycleList: [],
      selectedCycleId: '',
      failedCount: 0,
      successCount: 0,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    document.title = 'APAR Bulk Upload - ' + CONSTANT.organisation.name;
    getAppraisalCycleList().then((res: any) => {
      this.setState({ cycleList: res?.result || [] });
    }).catch(() => {});
  }

  switchTab = (tabKey: string) => {
    this.setState({ activeTab: tabKey, rawExcel: [], uploadComplete: false, failedCount: 0, successCount: 0 });
  };

  fileOnChange = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    const f = e.target.files?.[0];
    if (!f) return;

    const reader = new FileReader();
    this.setState({ uploadComplete: false, failedCount: 0, successCount: 0 });

    reader.onload = (evt: any) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: 'array', cellDates: true, cellNF: false, cellText: false });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const json: any[] = XLSX.utils.sheet_to_json(worksheet);

      const config = SECTION_CONFIG[this.state.activeTab];
      for (let i = 0; i < json.length; i++) {
        for (const col of config.columns) {
          json[i][col.key] = json[i][col.key] != null ? json[i][col.key].toString().trim() : '';
        }
        json[i].status = undefined;
        json[i].statusMsg = '';
      }
      this.setState({ rawExcel: json });
    };
    reader.readAsArrayBuffer(f);
  };

  validateExcel = () => {
    const { rawExcel, activeTab } = this.state;
    if (rawExcel.length === 0) return false;
    return rawExcel.every((item: any) => SECTION_CONFIG[activeTab].validate(item));
  };

  uploadData = async (e: any) => {
    e.preventDefault();
    if (!this.validateExcel()) return;

    const { rawExcel, activeTab, selectedCycleId } = this.state;
    const currentTab = TABS.find(t => t.key === activeTab);

    if (currentTab?.cycleBased && !selectedCycleId) {
      alert('Please select an appraisal cycle first.');
      return;
    }

    this.setState({ showLoader: true });
    const config = SECTION_CONFIG[activeTab];
    const payload = currentTab?.cycleBased
      ? { appraisalCycleId: selectedCycleId, data: rawExcel }
      : rawExcel;

    try {
      const res = await config.apiAction(payload);
      if (res?.status === 'success') {
        const updatedExcel = rawExcel.map((item: any, idx: number) => {
          const result = res.result?.[idx];
          return { ...item, status: result?.success !== false, statusMsg: result?.message || (result?.success !== false ? 'Uploaded successfully' : 'Failed') };
        });
        const failedCount = updatedExcel.filter((item: any) => item.status === false).length;
        const successCount = updatedExcel.length - failedCount;
        this.setState({ rawExcel: updatedExcel, showLoader: false, uploadComplete: true, failedCount, successCount });
        if (failedCount > 0) {
          alert(`Upload completed with errors.\nSuccessful: ${successCount}, Failed: ${failedCount}\nPlease review the error messages in the table.`);
        } else {
          alert('Upload completed successfully!');
        }
      } else {
        this.setState({ showLoader: false });
        alert(res?.error || 'Failed to upload. Please try again.');
      }
    } catch (err) {
      this.setState({ showLoader: false });
      alert('Failed to upload. Please try again.');
    }
  };

  downloadSample = () => {
    const { activeTab } = this.state;
    const config = SECTION_CONFIG[activeTab];
    const ws = XLSX.utils.json_to_sheet(config.sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, config.title);
    XLSX.writeFile(wb, `apar-${activeTab}-sample.xlsx`);
  };

  render() {
    const { rawExcel, showLoader, uploadComplete, activeTab, cycleList, selectedCycleId, failedCount, successCount } = this.state;
    const isValidExcel = this.validateExcel();
    const config = SECTION_CONFIG[activeTab];
    const currentTab = TABS.find(t => t.key === activeTab);
    const isCycleBased = currentTab?.cycleBased;
    const selectedCycle = cycleList.find((c: any) => c.id?.toString() === selectedCycleId?.toString());

    const cardShadow = "0 4px 14px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.04)";
    const cardBorder = "#e0e6ed";

    return (
      <div className="container-fluid" style={{ overflow: 'hidden' }}>
        <div className="row">
          <div className="col-md-12" style={{ minWidth: 0 }}>
            <div className="card homedob p-2 p-md-4 mb-4" style={{ border: `1px solid ${cardBorder}`, boxShadow: cardShadow, borderRadius: '10px', overflow: 'hidden', maxWidth: '100%' }}>

              {/* Header */}
              <div className="d-flex justify-content-between align-items-center flex-wrap mb-4" style={{ borderBottom: '1px solid #e0e6ed', paddingBottom: '15px' }}>
                <div>
                  <h1 className="page-title mb-1" style={{ fontSize: '22px' }}>
                    <i className="fa fa-cloud-upload me-2" style={{ color: '#3b82f6' }}></i>
                    APAR - Bulk Upload
                  </h1>
                  <p style={{ fontSize: '13px', color: '#7f8c8d', margin: 0 }}>Upload employee data in bulk using Excel files</p>
                </div>
              </div>

              {/* Cycle Selector */}
              <div style={{ background: '#f8fafc', border: `1px solid ${cardBorder}`, borderRadius: '10px', padding: '16px 20px', marginBottom: '20px', overflow: 'hidden' }}>
                <div className="row align-items-end">
                  <div className="col-md-5">
                    <label style={{ fontWeight: 600, fontSize: '13px', color: '#2c3e50', marginBottom: '6px', display: 'block' }}>
                      <i className="fa fa-calendar me-1" style={{ color: '#3b82f6' }}></i>
                      Appraisal Cycle <small className="text-danger">*</small>
                    </label>
                    <select
                      className="form-control"
                      value={selectedCycleId}
                      onChange={(e) => this.setState({ selectedCycleId: e.target.value })}
                      style={{ borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                    >
                      <option value="">-- Select Appraisal Cycle --</option>
                      {cycleList.map((cycle: any) => (
                        <option key={cycle.id} value={cycle.id}>
                          {cycle.cycle_name || cycle.cycleName || ''} ({formatDateDDMMYY(cycle.start_date || cycle.effectiveFrom)} to {formatDateDDMMYY(cycle.end_date || cycle.effectiveTo)}) - {cycle.status || ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedCycle && (
                    <div className="col-md-7">
                      <div className="d-flex align-items-center gap-3 flex-wrap" style={{ marginTop: '8px' }}>
                        <span style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: '12px', fontWeight: 600, padding: '5px 14px', borderRadius: '20px', border: '1px solid #bfdbfe' }}>
                          <i className="fa fa-calendar-check-o me-1"></i>
                          {formatDateDDMMYY(selectedCycle.start_date || selectedCycle.effectiveFrom)} to {formatDateDDMMYY(selectedCycle.end_date || selectedCycle.effectiveTo)}
                        </span>
                        <span style={{
                          background: (selectedCycle.status || '').toLowerCase() === 'active' ? '#ecfdf5' : '#fef9c3',
                          color: (selectedCycle.status || '').toLowerCase() === 'active' ? '#059669' : '#a16207',
                          fontSize: '11px', fontWeight: 700, padding: '4px 12px', borderRadius: '20px',
                          border: `1px solid ${(selectedCycle.status || '').toLowerCase() === 'active' ? '#a7f3d0' : '#fde68a'}`,
                        }}>
                          {selectedCycle.status || 'Draft'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="d-flex gap-2 flex-wrap mb-4">
                {TABS.map((tab) => {
                  const isActive = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      className="btn btn-sm"
                      onClick={() => this.switchTab(tab.key)}
                      style={{
                        background: isActive ? tab.color : '#fff',
                        color: isActive ? '#fff' : '#475569',
                        border: `1px solid ${isActive ? tab.color : '#cbd5e1'}`,
                        borderRadius: '8px',
                        padding: '8px 12px',
                        fontSize: '12px',
                        fontWeight: isActive ? 600 : 400,
                        boxShadow: isActive ? `0 2px 8px ${tab.color}40` : 'none',
                        transition: 'all 0.2s',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <i className={`fa ${tab.icon} me-1`}></i> {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Upload Card */}
              <div style={{ background: '#fff', border: `1px solid ${cardBorder}`, borderRadius: '10px', boxShadow: cardShadow, borderTop: `3px solid ${currentTab?.color || '#3b82f6'}`, overflow: 'hidden' }}>

                {/* Card Header */}
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', overflow: 'hidden' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h5 style={{ fontWeight: 600, margin: 0, fontSize: '16px', color: '#1e293b', wordBreak: 'break-word' }}>
                      <i className={`fa ${currentTab?.icon} me-2`} style={{ color: currentTab?.color }}></i>
                      {config.title}
                    </h5>
                    <p style={{ fontSize: '12px', color: '#94a3b8', margin: '2px 0 0 0' }}>
                      {isCycleBased
                        ? 'This data will be uploaded for the selected appraisal cycle'
                        : 'This is permanent employee data, not linked to any appraisal cycle'}
                    </p>
                  </div>
                  {!isCycleBased && (
                    <span style={{ background: '#fdf4ff', color: '#a855f7', fontSize: '11px', fontWeight: 600, padding: '4px 12px', borderRadius: '20px', border: '1px solid #e9d5ff' }}>
                      <i className="fa fa-info-circle me-1"></i> Cycle Independent
                    </span>
                  )}
                </div>

                {/* Card Body */}
                <div style={{ padding: '20px', overflow: 'hidden' }}>

                  {/* File Upload + Column Info */}
                  <div className="row mb-3">
                    <div className="col-md-5">
                      <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155', marginBottom: '6px', display: 'block' }}>
                        <i className="fa fa-file-excel-o me-1" style={{ color: '#059669' }}></i>
                        Upload Excel File <small className="text-danger">*</small>
                      </label>
                      <input
                        key={activeTab}
                        className="form-control"
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={this.fileOnChange}
                        style={{ borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', padding: '6px 10px' }}
                      />
                      <a className="c-pointer btn-link" onClick={this.downloadSample} style={{ fontSize: '12px', marginTop: '6px', display: 'inline-block' }}>
                        <i className="fa fa-download me-1"></i> Download sample file
                      </a>
                    </div>
                    <div className="col-md-7 mt-3 mt-md-0">
                      <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155', marginBottom: '6px', display: 'block' }}>
                        <i className="fa fa-columns me-1" style={{ color: '#3b82f6' }}></i>
                        Expected Columns
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxWidth: '100%' }}>
                        {config.columns.map((col: any) => (
                          <span
                            key={col.key}
                            style={{
                              background: col.required ? '#eff6ff' : '#f8fafc',
                              color: col.required ? '#1d4ed8' : '#64748b',
                              fontSize: '11px',
                              fontWeight: 600,
                              padding: '4px 10px',
                              borderRadius: '6px',
                              border: `1px solid ${col.required ? '#bfdbfe' : '#e2e8f0'}`,
                              wordBreak: 'break-all',
                            }}
                          >
                            {col.key}{col.required ? ' *' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Data Table */}
                  <div className="table-responsive" style={{ border: `1px solid ${cardBorder}`, borderRadius: '8px', overflowX: 'auto' }}>
                    <table className="table table-hover mb-0" style={{ fontSize: '12px', minWidth: '600px' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{ padding: '10px 12px', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>#</th>
                          <th style={{ padding: '10px 12px', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Status</th>
                          {uploadComplete && (
                            <th style={{ padding: '10px 12px', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0', minWidth: '200px' }}>Message</th>
                          )}
                          {config.columns.map((col: any) => (
                            <th key={col.key} style={{ padding: '10px 12px', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0', minWidth: '120px' }}>
                              {col.label}
                              {col.required && <span style={{ color: '#dc2626' }}> *</span>}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rawExcel.length === 0 && (
                          <tr>
                            <td colSpan={config.columns.length + (uploadComplete ? 3 : 2)} style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                              <i className="fa fa-inbox" style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }}></i>
                              No records found. Upload an Excel file to preview data.
                            </td>
                          </tr>
                        )}
                        {rawExcel.map((item: any, index: number) => (
                          <tr key={index} style={{ borderBottom: '1px solid #f1f5f9', background: item.status === false ? '#fff5f5' : 'transparent' }}>
                            <td style={{ padding: '8px 12px', color: '#64748b' }}>{index + 1}</td>
                            <td style={{ padding: '8px 12px' }}>
                              {item.status === undefined ? (
                                <i title="Ready to upload" className="fa fa-cloud-upload" style={{ color: '#3b82f6', fontSize: '14px' }} />
                              ) : item.status ? (
                                <i title={item.statusMsg} className="fa fa-check-circle" style={{ color: '#059669', fontSize: '14px' }} />
                              ) : (
                                <i title={item.statusMsg} className="fa fa-times-circle" style={{ color: '#dc2626', fontSize: '14px' }} />
                              )}
                            </td>
                            {uploadComplete && (
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: item.status === false ? '#dc2626' : '#059669', fontWeight: item.status === false ? 600 : 400 }}>
                                {item.statusMsg || '-'}
                              </td>
                            )}
                            {config.columns.map((col: any) => (
                              <td
                                key={col.key}
                                style={{
                                  padding: '8px 12px',
                                  background: col.required && !item[col.key] ? '#fef2f2' : 'transparent',
                                  color: col.required && !item[col.key] ? '#dc2626' : '#334155',
                                }}
                              >
                                {item[col.key] || <span style={{ color: '#cbd5e1' }}>-</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Footer */}
                  <div className="d-flex justify-content-between align-items-center flex-wrap mt-3" style={{ gap: '10px' }}>
                    <div>
                      {rawExcel.length > 0 && (
                        <span style={{ fontSize: '13px', color: '#64748b' }}>
                          <i className="fa fa-list-ol me-1"></i>
                          Total records: <strong style={{ color: '#1e293b' }}>{rawExcel.length}</strong>
                          {uploadComplete && failedCount === 0 && (
                            <span style={{ color: '#059669', marginLeft: '15px', fontWeight: 600 }}>
                              <i className="fa fa-check-circle me-1"></i> Upload completed
                            </span>
                          )}
                          {uploadComplete && failedCount > 0 && (
                            <>
                              <span style={{ color: '#059669', marginLeft: '15px', fontWeight: 600 }}>
                                <i className="fa fa-check-circle me-1"></i> {successCount} succeeded
                              </span>
                              <span style={{ color: '#dc2626', marginLeft: '12px', fontWeight: 600 }}>
                                <i className="fa fa-times-circle me-1"></i> {failedCount} failed
                              </span>
                            </>
                          )}
                        </span>
                      )}
                    </div>
                    <button
                      title={!isValidExcel ? 'Upload a valid excel first' : (isCycleBased && !selectedCycleId) ? 'Select an appraisal cycle' : ''}
                      disabled={!isValidExcel || uploadComplete || (isCycleBased && !selectedCycleId)}
                      className="btn btn-primary btn-sm"
                      onClick={this.uploadData}
                      style={{ borderRadius: '8px', padding: '8px 24px', fontSize: '13px', fontWeight: 600 }}
                    >
                      <i className="fa fa-cloud-upload me-1"></i> Upload Data
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
        {showLoader && <Loader />}
      </div>
    );
  }
}

export default BulkUploadAuthority;
